import { useEffect } from "react"
import type { AuthClaims } from "@domo/shared"
import {
    TOKEN_STORAGE_KEY,
    clearTokenFromFragment,
    decodeJwtClaimsUnsafe,
    makeDevToken,
    readTokenFromFragment,
} from "../lib/auth"
import { useAuthStore } from "../stores/useAuthStore"

// Hook bootstrap — corre una sola vez en App. Escribe en useAuthStore.
// Cualquier componente que necesite auth puede leer del store directamente.
export function useAuth() {
    const state = useAuthStore()

    useEffect(() => {
        try {
            // 1) Token recién bridged desde Framer (URL fragment).
            const fromFragment = readTokenFromFragment()
            if (fromFragment) {
                sessionStorage.setItem(TOKEN_STORAGE_KEY, fromFragment)
                clearTokenFromFragment()
                const claims = decodeJwtClaimsUnsafe(
                    fromFragment
                ) as AuthClaims | null
                useAuthStore.getState().set({
                    status: "authenticated",
                    token: fromFragment,
                    claims,
                })
                return
            }

            // 2) Sesión previa en sessionStorage (mismo tab).
            const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY)
            if (stored) {
                const claims = decodeJwtClaimsUnsafe(
                    stored
                ) as AuthClaims | null
                useAuthStore.getState().set({
                    status: "authenticated",
                    token: stored,
                    claims,
                })
                return
            }

            // 3) Dev fallback en local: ?dev=Zak fabrica un dev-token efímero.
            const url = new URL(window.location.href)
            const dev = url.searchParams.get("dev")
            if (dev && import.meta.env.DEV) {
                const devToken = makeDevToken(dev)
                sessionStorage.setItem(TOKEN_STORAGE_KEY, devToken)
                useAuthStore.getState().set({
                    status: "authenticated",
                    token: devToken,
                    claims: {
                        sub: devToken.split(":")[1],
                        displayName: dev,
                        siliconIndex: 50,
                    },
                })
                return
            }

            useAuthStore.getState().set({
                status: "anonymous",
                token: null,
                claims: null,
            })
        } catch (err) {
            const msg = err instanceof Error ? err.message : "auth_init_failed"
            useAuthStore.getState().set({
                status: "error",
                token: null,
                claims: null,
                error: msg,
            })
        }
    }, [])

    return state
}
